import { Controller } from "@hotwired/stimulus"

// QAN-700 Shelf Corp dashboard.
//
// Same interaction grammar as compliance_dashboard_controller: inline edits POST
// JSON and the page reloads when the change can move a row between buckets. The
// admin runs Turbolinks rather than Turbo, so these are plain fetch calls, not
// Turbo Streams.
//
// Drives both the index (grid + kanban) and the order detail page; targets are
// guarded because the detail page has no view toggle.
const STORAGE_KEY = "shelfCorpDashboardView"

// The deliverable states that mean "this is required of the order".
const REQUIRED_STATES = ["required", "in_process", "complete"]

export default class extends Controller {
  static targets = ["tableView", "cardView", "tableButton", "cardButton", "modal", "modalBody"]

  connect() {
    if (!this.hasTableViewTarget) return
    const stored = this.readStoredView()
    if (stored === "card") this.showCard()
    else this.showTable()
  }

  readStoredView() {
    // Browser storage can throw in a private window or with site data blocked.
    try { return window.localStorage.getItem(STORAGE_KEY) } catch (e) { return null }
  }

  storeView(value) {
    try { window.localStorage.setItem(STORAGE_KEY, value) } catch (e) { /* non-fatal */ }
  }

  showTable() {
    if (!this.hasTableViewTarget) return
    this.tableViewTarget.classList.remove("hidden")
    this.cardViewTarget.classList.add("hidden")
    this.setActive(this.tableButtonTarget, this.cardButtonTarget)
    this.storeView("table")
  }

  showCard() {
    if (!this.hasCardViewTarget) return
    this.cardViewTarget.classList.remove("hidden")
    this.tableViewTarget.classList.add("hidden")
    this.setActive(this.cardButtonTarget, this.tableButtonTarget)
    this.storeView("card")
  }

  setActive(on, off) {
    if (on) on.classList.add("active")
    if (off) off.classList.remove("active")
  }

  // --- inline edits -------------------------------------------------------

  // Stage is the one axis the team edits directly; Status derives from it.
  // Selecting On Hold opens the reason-gated modal instead of saving, mirroring
  // the Compliance grid.
  async saveStage(event) {
    const button = event.currentTarget
    const row = button.closest("[data-shelf-corp-row]")
    const select = row.querySelector("[data-shelf-corp-stage-select]")
    if (!select) return

    if (select.value === "On Hold") {
      this.showModalTemplate(button.dataset.holdModalTemplate)
      return
    }

    this.setFeedback(row, "Saving…", "text-gray-500")
    const body = { business_loan_service: { stage: select.value } }
    const result = await this.send(button.dataset.url, "POST", body, row)
    if (result) window.location.reload()
  }

  async saveStateFormed(event) {
    const select = event.currentTarget
    const row = select.closest("[data-shelf-corp-row]")
    this.setFeedback(row, "Saving…", "text-gray-500")
    const body = { business_loan_service: { state_formed: select.value } }
    const result = await this.send(select.dataset.url, "POST", body, row)
    if (result) this.setFeedback(row, "Saved", "text-green-600")
  }

  async saveOrderOwner(event) {
    const select = event.currentTarget
    const row = select.closest("[data-shelf-corp-row]")
    this.setFeedback(row, "Saving…", "text-gray-500")
    const body = { business_loan_service: { order_owner_id: select.value } }
    const result = await this.send(select.dataset.url, "POST", body, row)
    if (result) this.setFeedback(row, "Saved", "text-green-600")
  }

  async saveOrderNote(event) {
    const field = event.currentTarget
    const row = field.closest("[data-shelf-corp-row]") || this.element
    const body = { business_loan_service: { order_note: field.value } }
    const result = await this.send(field.dataset.url, "POST", body, row)
    if (result) this.setFeedback(row, "Saved", "text-green-600")
  }

  // Required? and Status are two controls over ONE 5-state column. Required?
  // sets the floor (not_set / not_required / required); Status refines it within
  // the required band. Reload because completing the last one can auto-complete
  // the whole order, which changes Status, Stage and the banner.
  async saveDeliverable(event) {
    const select = event.currentTarget
    const tr = select.closest("[data-deliverable-row]")
    const row = this.element
    const column = tr.dataset.deliverable

    let value
    if (select.hasAttribute("data-deliverable-required")) {
      value = select.value
      // Moving into "Required" from an unassessed/not-required state starts the
      // work at `required`, not at whatever the disabled Status select showed.
      if (value === "required") {
        const statusSelect = tr.querySelector("[data-deliverable-status]")
        const current = statusSelect && REQUIRED_STATES.includes(statusSelect.value) ? statusSelect.value : "required"
        value = current
      }
    } else {
      value = select.value
    }
    if (!value) return

    this.setFeedback(row, "Saving…", "text-gray-500")
    const body = { business_loan_service: { [column]: value } }
    const result = await this.send(select.dataset.url, "POST", body, row)
    if (result) window.location.reload()
  }

  // Uploads go straight into the client's Documents tab; a success can complete
  // the order, so reload rather than patching the row in place.
  async uploadDeliverable(event) {
    event.preventDefault()
    const form = event.currentTarget
    const input = form.querySelector('input[type="file"]')
    if (!input || !input.files || input.files.length === 0) return

    const row = this.element
    this.setFeedback(row, "Uploading…", "text-gray-500")

    const data = new FormData()
    data.append("file", input.files[0])
    data.append("deliverable", form.querySelector('input[name="deliverable"]').value)

    const token = document.querySelector('meta[name="csrf-token"]')
    try {
      const response = await fetch(form.dataset.url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": token ? token.content : "", "Accept": "application/json" },
        body: data
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.setFeedback(row, (payload.errors && payload.errors[0]) || "Upload failed", "text-red-600")
        return
      }
      window.location.reload()
    } catch (e) {
      this.setFeedback(row, "Upload failed", "text-red-600")
    }
  }

  // --- modals -------------------------------------------------------------

  openModal(event) {
    this.showModalTemplate(event.currentTarget.dataset.modalTemplate)
  }

  showModalTemplate(id) {
    if (!id || !this.hasModalTarget) return
    const template = document.getElementById(id)
    if (!template) return
    this.modalBodyTarget.innerHTML = template.innerHTML
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
  }

  closeModal() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.modalBodyTarget.innerHTML = ""
  }

  async submitModal(event) {
    event.preventDefault()
    const form = event.currentTarget
    const body = {}
    form.querySelectorAll("[name]").forEach((field) => { body[field.name] = field.value })

    const result = await this.send(form.action, (form.dataset.method || "post").toUpperCase(), body, null)
    if (result) {
      window.location.reload()
      return
    }
    const error = form.querySelector("[data-modal-error]")
    if (error) {
      error.textContent = this.lastError || "Could not save — please try again."
      error.classList.remove("hidden")
    }
  }

  // --- plumbing -----------------------------------------------------------

  async send(url, method, body, row) {
    this.lastError = null
    const token = document.querySelector('meta[name="csrf-token"]')
    try {
      const response = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token ? token.content : "",
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        // Surface the server's message rather than a generic one: the most
        // common failure here is the completion guard, whose message names the
        // deliverable that is blocking.
        this.lastError = (payload.errors && payload.errors[0]) || null
        if (row) this.setFeedback(row, this.lastError || "Error", "text-red-600")
        return null
      }
      return payload
    } catch (e) {
      if (row) this.setFeedback(row, "Error", "text-red-600")
      return null
    }
  }

  feedbackFor(row) {
    if (!row) return null
    return row.querySelector("[data-field-feedback]")
  }

  setFeedback(row, text, className) {
    const sink = this.feedbackFor(row)
    if (!sink) return
    sink.textContent = text
    sink.className = `text-xs ${className}`
    if (text === "Saved") {
      setTimeout(() => { if (sink.textContent === "Saved") sink.textContent = "" }, 2000)
    }
  }
}
